import { api } from "./config";

function qs(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

// Drawer balances + branch-cash transactions for the logged-in user's stations.
export async function getMyBranchCash(params) {
  try {
    const res = await api().get(`/branch-cash/my${qs(params)}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Deposit branch cash to the bank. `formData` carries stationId, currency, amount
// and the bank slip image (field `evidence_0`). Only the user's own station.
export async function branchCashDeposit(formData) {
  try {
    const res = await api().post("/branch-cash/deposit", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Single transaction detail — same shape as api/financial's getTransactionDetail,
// but restricted server-side to a branch_cash leg at one of the user's own stations.
export async function getBranchCashTransactionDetail(id) {
  try {
    const res = await api().get(`/branch-cash/transactions/${id}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Single top-up detail — same shape as api/financial's getTopupDetail, but
// restricted server-side to a top-up at one of the user's own stations.
export async function getMyTopupDetail(id) {
  try {
    const res = await api().get(`/branch-cash/topups/${id}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Visits (ShippingPayment) + cash movements + KPIs for one station/day. `params`
// may carry `stationId` (required) and `date` (defaults server-side to today).
export async function getMyDailySummary(params) {
  try {
    const res = await api().get(`/branch-cash/my/daily-summary${qs(params)}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Record a cash expense (e.g. buying drinking water) at the caller's own station.
// `formData` carries stationId, currency, amount, reason and the receipt photo
// (field `receiptPhoto`). Settles immediately under the per-currency threshold;
// otherwise files a pending_approval request — see `status` in the response.
export async function branchCashExpense(formData) {
  try {
    const res = await api().post("/branch-cash/expense", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Expense detail — same shape as api/financial's getBranchExpenseDetail, restricted
// server-side to a station the caller is assigned to.
export async function getMyBranchExpenseDetail(id) {
  try {
    const res = await api().get(`/branch-cash/expenses/${id}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Record a service charge (e.g. bulk delivery, shipping parcels back to Thailand —
// a service with no fixed rate-table price) at the caller's own station.
// `formData` carries stationId, currency, amount, purpose, an optional customerId,
// and the evidence photo (field `evidencePhoto`). Settles immediately under
// APPROVAL_THRESHOLD_THB (converted); otherwise files a pending_approval request —
// see `status` in the response.
export async function branchCashServiceCharge(formData) {
  try {
    const res = await api().post("/branch-cash/service-charge", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Service charge detail — same shape as api/financial's getServiceChargeDetail,
// restricted server-side to a station the caller is assigned to.
export async function getMyServiceChargeDetail(id) {
  try {
    const res = await api().get(`/branch-cash/service-charges/${id}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Evidence image URL (same-origin; httpOnly auth cookie is sent automatically),
// scoped the same way as getBranchCashTransactionDetail.
export const branchCashEvidenceUrl = (filename) =>
  filename ? `${api().defaults.baseURL || ""}/branch-cash/evidence/${filename}` : "";
